import { useState, useEffect, Fragment, useContext } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Title from '../../shared/Title';
import FFPaper from '../../shared/FFPaper';
import ChartContainer from '../../shared/ChartContainer';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import KebabMenu, { defaultMenus } from '../../shared/KebabMenu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TrafficAnalyticsContext } from '../../../context/trafficanalytics-context';
import BlueSkeleton from '../../shared/BlueSkeleton';

const handler = (menu: string) => {
    console.log(menu);
}

interface BarChartProps {
    data?: any[];
}

function TrafficBarChart({ data }: BarChartProps) {
    const theme = useTheme();

    const formatter = (value: any) => { return [value] };

    return (
        <ResponsiveContainer width="100%" height="100%" >
            <BarChart
                width={500}
                height={300}
                data={data}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                }}
                barSize={26}
            >
                <CartesianGrid stroke={theme.palette.shadow.dark} strokeDasharray="3 3" vertical={false} />
                <XAxis
                    dataKey="key"
                    stroke={theme.palette.text.primary}
                    tickLine={false}
                    axisLine={false}
                    strokeDasharray="3 3"
                    tickFormatter={(val) => 'L-' + val}
                    dy={5}
                    style={{
                        fontSize: '0.8rem',
                        fontFamily: 'Montserrat',
                        fontWeight: 100
                    }}
                />
                <YAxis
                    stroke={theme.palette.text.primary}
                    tickLine={false}
                    axisLine={false}
                    style={{
                        fontSize: '0.7rem',
                        fontFamily: 'Montserrat',
                        fontWeight: 100
                    }}
                />
                <Tooltip cursor={false} formatter={formatter} />
                <Bar dataKey="value" fill={theme.palette.secondary.main} radius={[8, 8, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    )
}



const WhiteBorderTextField = styled(TextField)`

  .MuiOutlinedInput-notchedOutline {
    border-color: white;
  }
  & .MuiOutlinedInput {
      border-color: white;
      
    &.Mui-focused fieldset {
      border-color: white;
      
    }
    &.MuiOutlinedInput-notchedOutline {
        border-color: white;
    }
  }
  
`;

export default function TrafficBarCard() {

    const perPage = 8;
    const [currentPage, setPage] = useState(1);
    const [pagination, setPagination] = useState('');
    const [start, setStart] = useState(0);
    const [end, setEnd] = useState(perPage);
    const { status, data, isFetching } = useContext(TrafficAnalyticsContext);
    const total = data?.trafficAnalyticsEntityTraffic.value.length;
    const totalPage = total ? (total / perPage) : 1;
    const chartData = data?.trafficAnalyticsEntityTraffic.value;

    function nextPage() {
        if (currentPage === totalPage) {
            return;
        }
        setPage(currentPage + 1);
    }

    function prevPage() {
        if (currentPage === 1) {
            return;
        }
        setPage(currentPage - 1);
    }

    useEffect(() => {
        if (total) {
            if (total <= perPage) {
                setEnd(total);
                return;
            }
            const startPage = ((currentPage - 1) * perPage) + 1;
            const endPage = startPage + (perPage - 1) > total ? total : startPage + (perPage - 1);
            setPagination(`${startPage}-${endPage} of ${total}`)
            setStart(startPage - 1);
            setEnd(endPage - 1);
        }

    }, [total, currentPage]);

    return (

        <Grid item={true} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <FFPaper sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column', 
                minHeight: 320,
                alignItems: 'stretch',
                position: 'relative',
                justifyContent: 'stretch',
                border: '0 0 0 0 solid',
            }}>
                <KebabMenu
                    menus={defaultMenus}
                    handler={handler}
                />
                <Stack 
                    sx={{
                        justifyContent: 'stretch',
                        height: '100%',
                        minHeight: 200,
                        flexDirection: {
                            xs: 'column',
                            md: 'row'
                        }
                    }}
                >
                    <Stack sx={{
                        p: 2,
                        maxHeight: 280,
                        borderRadius: 5,
                        backgroundColor: 'shadow.light',
                        height: '100%',
                        width: {
                            xs: '100%',    
                            md: '28%'
                        },
                        background: 'linear-gradient(to bottom, #F5F7FA 80%, #D5DDEA9F 100%)'
                    }}>
                        <Box sx={{
                            height: '20%'
                        }}>
                            <WhiteBorderTextField
                                sx={{
                                    backgroundColor: 'white',
                                    borderRadius: 4,
                                    borderBottomColor: 'white',
                                    width: '100%'
                                }}
                                placeholder='Search'
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="secondary"
                                                fontSize='large'
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{
                            height: '80%',
                            padding: '0px 0px 10px 20px',
                            overflowY: 'scroll',
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'text.primary #F5F7FA',
                            mt: 2,
                        }}>
                            {
                                chartData?.map((location: any) =>
                                    <Typography key={location.key} sx={{ p: 1 }}>Location {location.key}</Typography>
                                )
                            }
                        </Box>
                    </Stack>

                    <Stack direction="column"
                        spacing={{ xs: 1, sm: 2, md: 4 }}
                        sx={{
                            height: '100%',
                            padding: '10px 10px 10px 10px',
                            minHeight: 280,
                            width: {
                                xs: '100%',    
                                md: '68%'
                            },
                        }}
                    >
                        <Box
                            sx={{
                                pl: 3,
                                pr: 3,
                            }}
                        >
                            <Stack direction="row">
                                <Title info="This graph represents traffic at each individual entity included within filter settings">Entity Traffic</Title>
                                {(totalPage > 1) &&
                                    <Fragment>
                                        <Typography fontSize="small" color="secondary" sx={{ pr: 1 }}>{pagination}</Typography>
                                        <ChevronLeftIcon fontSize="small" color="secondary" onClick={prevPage} />
                                        <ChevronRightIcon fontSize="small" color="secondary" onClick={nextPage} />
                                    </Fragment>
                                }

                            </Stack>
                        </Box>

                        {isFetching && <BlueSkeleton width={'100%'} height={'100%'} sx={{ ml: 'auto', mr: 'auto' }} />}

                        {status === 'success' && !isFetching && 
                            <ChartContainer>
                                <TrafficBarChart data={chartData?.slice(start, end)} />
                            </ChartContainer>
                        }
                    </Stack>
                </Stack>
            </FFPaper>
        </Grid>
    )

}
